import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { DollarSign, TrendingUp, TrendingDown, Camera, SlidersHorizontal, History } from 'lucide-react';

const NetWorthChart = ({ session, currentNetWorth, totalAssets, totalLiabilities, netWorthSummary }) => {
  const [snapshots, setSnapshots] = useState([]);
  // snapshotDetails removed as it's not currently used in the component
  const [preferences, setPreferences] = useState({
    snapshot_frequency: 'monthly'
  });
  const [showSettings, setShowSettings] = useState(false);
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [showSnapshotHistory, setShowSnapshotHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hoveredSnapshot, setHoveredSnapshot] = useState(null);
  const [snapshotForm, setSnapshotForm] = useState({
    title: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchSnapshots();
    fetchPreferences();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSnapshots = async () => {
    try {
      const { data, error } = await supabase
        .from('net_worth_snapshots')
        .select('*')
        .eq('user_id', session.user.id)
        .order('snapshot_date', { ascending: false });

      if (error) throw error;
      setSnapshots(data || []);
    } catch (error) {
      console.error('Error fetching snapshots:', error);
    }
  };

  const fetchPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', session.user.id)
        .single();

      if (!error && data) {
        setPreferences(data);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const saveSnapshot = async () => {
    setLoading(true);
    try {
      const selectedDate = snapshotForm.date;
      const defaultTitle = snapshotForm.title || `Net Worth Snapshot - ${new Date(selectedDate).toLocaleDateString()}`;
      
      const { error } = await supabase
        .from('net_worth_snapshots')
        .insert({
          user_id: session.user.id,
          net_worth: currentNetWorth,
          total_assets: totalAssets,
          total_liabilities: totalLiabilities,
          snapshot_date: selectedDate,
          snapshot_title: defaultTitle,
          snapshot_notes: snapshotForm.notes
        });

      if (error) throw error;

      // Update preferences with last snapshot date
      await supabase
        .from('user_preferences')
        .upsert({
          user_id: session.user.id,
          snapshot_frequency: preferences.snapshot_frequency,
          last_snapshot_date: selectedDate
        });

      setSnapshotForm({ title: '', notes: '', date: new Date().toISOString().split('T')[0] });
      setShowSnapshotModal(false);
      fetchSnapshots();
    } catch (error) {
      console.error('Error saving snapshot:', error);
      alert('Error saving snapshot: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteSnapshot = async (id) => {
    if (!window.confirm('Are you sure you want to delete this snapshot?')) return;

    try {
      const { error } = await supabase
        .from('net_worth_snapshots')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchSnapshots();
    } catch (error) {
      console.error('Error deleting snapshot:', error);
      alert('Error deleting snapshot: ' + error.message);
    }
  };

  const updatePreferences = async (newFrequency) => {
    try {
      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: session.user.id,
          snapshot_frequency: newFrequency,
          last_snapshot_date: preferences.last_snapshot_date
        });

      if (error) throw error;
      setPreferences(prev => ({ ...prev, snapshot_frequency: newFrequency }));
    } catch (error) {
      console.error('Error updating preferences:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const chartSnapshots = [...snapshots].sort((a, b) => new Date(a.snapshot_date) - new Date(b.snapshot_date));

  const getChartXAxisLabels = () => {
    const frequency = preferences.snapshot_frequency;
    
    switch (frequency) {
      case 'monthly':
        return chartSnapshots.map(snapshot => 
          new Date(snapshot.snapshot_date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
        );
      case 'quarterly':
        return chartSnapshots.map(snapshot => {
          const date = new Date(snapshot.snapshot_date);
          const quarter = Math.floor(date.getMonth() / 3) + 1;
          return `Q${quarter} '${date.getFullYear().toString().slice(-2)}`;
        });
      case 'semi-annually':
        return chartSnapshots.map(snapshot => {
          const date = new Date(snapshot.snapshot_date);
          const half = date.getMonth() < 6 ? 'H1' : 'H2';
          return `${half} '${date.getFullYear().toString().slice(-2)}`;
        });
      case 'annually':
        return chartSnapshots.map(snapshot => 
          new Date(snapshot.snapshot_date).getFullYear().toString()
        );
      default:
        return chartSnapshots.map(snapshot => formatDate(snapshot.snapshot_date));
    }
  };

  const xAxisLabels = getChartXAxisLabels();

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Net Worth</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(netWorthSummary.netWorth)}</p>
              <p className="text-xs text-gray-400 mt-1">As of {netWorthSummary.asOfDate}</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
              <DollarSign className="h-5 w-5 text-primary-600" />
            </div>
          </div>
          <button onClick={() => setShowSnapshotModal(true)} className="btn-primary btn-sm w-full mt-4">
            <Camera className="h-4 w-4 mr-1.5" /> Take Snapshot
          </button>
        </div>

        <div className="card p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Assets</p>
              <p className="text-2xl font-bold text-success-600 mt-1">{formatCurrency(netWorthSummary.totalAssets)}</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-success-50 flex items-center justify-center shrink-0">
              <TrendingUp className="h-5 w-5 text-success-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Liabilities</p>
              <p className="text-2xl font-bold text-danger-600 mt-1">{formatCurrency(netWorthSummary.totalLiabilities)}</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-danger-50 flex items-center justify-center shrink-0">
              <TrendingDown className="h-5 w-5 text-danger-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Chart card */}
      <div className="card">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Net Worth Tracking</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="btn-outline btn-sm"
              >
                <SlidersHorizontal className="h-4 w-4 mr-1.5" /> Frequency
              </button>
              <button
                onClick={() => setShowSnapshotHistory(!showSnapshotHistory)}
                className="btn-outline btn-sm"
              >
                <History className="h-4 w-4 mr-1.5" /> History ({snapshots.length})
              </button>
            </div>
          </div>

          {showSettings && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-medium text-gray-900 mb-3">Snapshot Frequency Preference</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {['monthly', 'quarterly', 'semi-annually', 'annually'].map((freq) => (
                  <button
                    key={freq}
                    onClick={() => updatePreferences(freq)}
                    className={`px-3 py-2 text-sm rounded-md border transition-colors duration-200 ${
                      preferences.snapshot_frequency === freq
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {freq.charAt(0).toUpperCase() + freq.slice(1).replace('-', ' ')}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Enhanced Chart */}
          <div className="relative">
            <div className="h-80 bg-gray-50 rounded-lg p-4 relative overflow-hidden">
              <svg viewBox="0 0 400 240" className="w-full h-full">
                {/* Background grid */}
                <defs>
                  <pattern id="grid" width="40" height="24" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 24" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />

                {chartSnapshots.length > 0 && (
                  <>
                    {/* Y-axis labels (Net Worth values) */}
                    {(() => {
                      const maxValue = Math.max(...chartSnapshots.map(s => s.net_worth));
                      const minValue = Math.min(...chartSnapshots.map(s => s.net_worth));
                      const range = maxValue - minValue;
                      const padding = range * 0.1;
                      const adjustedMax = maxValue + padding;
                      const adjustedMin = minValue - padding;
                      const steps = 5;
                      const stepValue = (adjustedMax - adjustedMin) / steps;
                      
                      return Array.from({ length: steps + 1 }, (_, i) => {
                        const value = adjustedMin + (stepValue * i);
                        const y = 200 - ((value - adjustedMin) / (adjustedMax - adjustedMin)) * 160;
                        return (
                          <g key={i}>
                            <line x1="40" y1={y} x2="360" y2={y} stroke="#d1d5db" strokeWidth="1" strokeDasharray="2,2" />
                            <text x="35" y={y + 4} textAnchor="end" fontSize="10" fill="#6b7280" fontFamily="monospace">
                              {formatCurrency(value).replace('$', '$').replace('.00', '')}
                            </text>
                          </g>
                        );
                      });
                    })()}

                    {/* X-axis labels (Dates) */}
                    {chartSnapshots.map((snapshot, index) => {
                      const x = 50 + (index / Math.max(chartSnapshots.length - 1, 1)) * 300;
                      const showLabel = chartSnapshots.length <= 6 || index % Math.ceil(chartSnapshots.length / 6) === 0;
                      
                      if (!showLabel && index !== chartSnapshots.length - 1) return null;
                      
                      return (
                        <text 
                          key={index}
                          x={x} 
                          y="225" 
                          textAnchor="middle" 
                          fontSize="10" 
                          fill="#6b7280"
                          fontFamily="system-ui"
                          fontWeight="500"
                        >
                          {xAxisLabels[index]}
                        </text>
                      );
                    })}

                    {/* Chart line */}
                    {(() => {
                      const maxValue = Math.max(...chartSnapshots.map(s => s.net_worth));
                      const minValue = Math.min(...chartSnapshots.map(s => s.net_worth));
                      const range = maxValue - minValue || 1;
                      const padding = range * 0.1;
                      const adjustedMax = maxValue + padding;
                      const adjustedMin = minValue - padding;
                      const adjustedRange = adjustedMax - adjustedMin;

                      const points = chartSnapshots.map((snapshot, index) => {
                        const x = 50 + (index / Math.max(chartSnapshots.length - 1, 1)) * 300;
                        const y = 200 - ((snapshot.net_worth - adjustedMin) / adjustedRange) * 160;
                        return `${x},${y}`;
                      });

                      return (
                        <>
                          {/* Line path */}
                          <path
                            d={`M ${points.join(' L ')}`}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          
                          {/* Data points */}
                          {chartSnapshots.map((snapshot, index) => {
                            const x = 50 + (index / Math.max(chartSnapshots.length - 1, 1)) * 300;
                            const y = 200 - ((snapshot.net_worth - adjustedMin) / adjustedRange) * 160;
                            const isHovered = hoveredSnapshot === snapshot.id;
                            
                            return (
                              <circle
                                key={snapshot.id}
                                cx={x}
                                cy={y}
                                r={isHovered ? 8 : 6}
                                fill={isHovered ? "#1d4ed8" : "#3b82f6"}
                                stroke="white"
                                strokeWidth="2"
                                className="cursor-pointer transition-all duration-200"
                                onMouseEnter={() => setHoveredSnapshot(snapshot.id)}
                                onMouseLeave={() => setHoveredSnapshot(null)}
                              />
                            );
                          })}
                        </>
                      );
                    })()}
                  </>
                )}

                {chartSnapshots.length === 0 && (
                  <text x="200" y="120" textAnchor="middle" fontSize="14" fill="#9ca3af">
                    Take your first snapshot to see your progress!
                  </text>
                )}
              </svg>

              {/* Hover tooltip */}
              {hoveredSnapshot && (
                <div className="absolute top-4 left-4 bg-white p-3 rounded-lg shadow-lg border border-gray-200 z-10 max-w-xs">
                  {(() => {
                    const snapshot = chartSnapshots.find(s => s.id === hoveredSnapshot);
                    if (!snapshot) return null;
                    
                    return (
                      <>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {snapshot.snapshot_title || 'Net Worth Snapshot'}
                        </h4>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div><strong>Date:</strong> {formatDate(snapshot.snapshot_date)}</div>
                          <div><strong>Net Worth:</strong> {formatCurrency(snapshot.net_worth)}</div>
                          <div><strong>Assets:</strong> {formatCurrency(snapshot.total_assets)}</div>
                          <div><strong>Liabilities:</strong> {formatCurrency(snapshot.total_liabilities)}</div>
                          {snapshot.snapshot_notes && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                              <strong>Notes:</strong> {snapshot.snapshot_notes}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Chart Statistics */}
            {chartSnapshots.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-gray-600">Highest</div>
                  <div className="font-semibold text-green-600">
                    {formatCurrency(Math.max(...chartSnapshots.map(s => s.net_worth)))}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-gray-600">Lowest</div>
                  <div className="font-semibold text-red-600">
                    {formatCurrency(Math.min(...chartSnapshots.map(s => s.net_worth)))}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-gray-600">Total Change</div>
                  <div className="font-semibold text-blue-600">
                    {formatCurrency(
                      chartSnapshots[chartSnapshots.length - 1]?.net_worth - chartSnapshots[0]?.net_worth || 0
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Snapshot History */}
          {showSnapshotHistory && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
              <h3 className="font-medium text-gray-900 mb-3">Snapshot History</h3>
              {snapshots.length === 0 ? (
                <p className="text-gray-500 text-sm">No snapshots yet. Take your first snapshot to track your progress!</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {snapshots.map((snapshot) => (
                    <div key={snapshot.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-200 group">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {snapshot.snapshot_title || 'Net Worth Snapshot'}
                        </h4>
                        <button
                          onClick={() => deleteSnapshot(snapshot.id)}
                          className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-all duration-200 text-xs"
                        >
                          ×
                        </button>
                      </div>
                      <div className="space-y-1 text-xs text-gray-600">
                        <div><strong>Date:</strong> {formatDate(snapshot.snapshot_date)}</div>
                        <div><strong>Net Worth:</strong> <span className="text-blue-600 font-semibold">{formatCurrency(snapshot.net_worth)}</span></div>
                        <div><strong>Assets:</strong> <span className="text-green-600">{formatCurrency(snapshot.total_assets)}</span></div>
                        <div><strong>Liabilities:</strong> <span className="text-red-600">{formatCurrency(snapshot.total_liabilities)}</span></div>
                        {snapshot.snapshot_notes && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-xs italic">
                            <strong>Notes:</strong> {snapshot.snapshot_notes}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          Created: {new Date(snapshot.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Snapshot Modal */}
      {showSnapshotModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Take Net Worth Snapshot</h3>
            
            <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
              <div className="text-sm text-gray-700">Current Net Worth:</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(currentNetWorth)}</div>
              <div className="text-xs text-gray-600 mt-1">
                This value will be saved for the selected date
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Snapshot Date *
                </label>
                <input
                  type="date"
                  value={snapshotForm.date}
                  onChange={(e) => setSnapshotForm(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Snapshot Title (Optional)
                </label>
                <input
                  type="text"
                  value={snapshotForm.title}
                  onChange={(e) => setSnapshotForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={`Net Worth Snapshot - ${new Date(snapshotForm.date).toLocaleDateString()}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={snapshotForm.notes}
                  onChange={(e) => setSnapshotForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any notes about this snapshot..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSnapshotModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={saveSnapshot}
                disabled={loading || !snapshotForm.date}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-md transition-colors duration-200"
              >
                {loading ? 'Saving...' : 'Take Snapshot'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetWorthChart;